import { useEffect, useRef, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { AlertCircle, CheckCircle2, Loader2 } from "lucide-react";
import { AccountPage, validateTokenSearch } from "@/components/auth/AccountPage";
import { accountAPI } from "@/lib/accountApi";
import { useAuth } from "@/contexts/AuthContext";

export const Route = createFileRoute("/konto/bestaetigen")({
  validateSearch: validateTokenSearch,
  component: VerifyEmailPage,
});

type VerifyState =
  | { status: "verifying" }
  | { status: "verified"; email: string }
  | { status: "failed"; detail: string };

const LOGIN_NOTICE = "E-Mail bestätigt. Sie können sich jetzt anmelden.";

function VerifyEmailPage() {
  const { token } = Route.useSearch();
  const navigate = useNavigate();
  const { openAuthModal, subscribe } = useAuth();
  const [state, setState] = useState<VerifyState>(
    token
      ? { status: "verifying" }
      : { status: "failed", detail: "Der Link ist unvollständig. Bitte öffnen Sie ihn erneut aus der E-Mail." }
  );
  // A token works once, and StrictMode runs effects twice in development.
  const startedRef = useRef(false);

  useEffect(() => {
    if (!token || startedRef.current) return;
    startedRef.current = true;
    // Keep the token out of the browser history.
    void navigate({ to: "/konto/bestaetigen", search: {}, replace: true });
    void accountAPI.verify(token).then((result) => {
      if (result.ok) {
        const email = result.data?.email ?? "";
        setState({ status: "verified", email });
        openAuthModal("login", { email, notice: LOGIN_NOTICE });
      } else {
        setState({ status: "failed", detail: result.detail });
      }
    });
  }, [token, navigate, openAuthModal]);

  // Signed in from here, go on to the chat.
  useEffect(
    () =>
      subscribe((change) => {
        if (change === "login") void navigate({ to: "/" });
      }),
    [subscribe, navigate]
  );

  return (
    <AccountPage title="E-Mail bestätigen">
      {state.status === "verifying" && (
        <div className="flex items-center gap-3 text-gray-700">
          <Loader2 className="h-5 w-5 animate-spin text-purple-600" />
          <p>Ihre E-Mail-Adresse wird bestätigt...</p>
        </div>
      )}

      {state.status === "verified" && (
        <div className="space-y-6">
          <div className="flex items-start gap-3 rounded-xl bg-green-50 border border-green-200 p-4">
            <CheckCircle2 className="h-6 w-6 text-green-600 shrink-0" />
            <div>
              <p className="font-medium text-green-800">E-Mail bestätigt</p>
              <p className="text-sm text-green-700 mt-1">
                Ihr Konto ist aktiv. Melden Sie sich an, um Ihre gespeicherten Chats zu sehen.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => openAuthModal("login", { email: state.email, notice: LOGIN_NOTICE })}
            className="px-6 py-2.5 text-sm font-medium rounded-lg text-white shadow-xs bg-linear-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 hover:shadow-md transition-all"
          >
            Anmelden
          </button>
        </div>
      )}

      {state.status === "failed" && (
        <div className="flex items-start gap-3 rounded-xl bg-red-50 border border-red-200 p-4">
          <AlertCircle className="h-6 w-6 text-red-600 shrink-0" />
          <div>
            <p className="font-medium text-red-800">Bestätigung fehlgeschlagen</p>
            <p className="text-sm text-red-700 mt-1">{state.detail}</p>
            <p className="text-sm text-red-700 mt-2">
              Melden Sie sich mit Ihrer E-Mail-Adresse und Ihrem Passwort an, dann senden wir
              Ihnen einen neuen Link.
            </p>
          </div>
        </div>
      )}
    </AccountPage>
  );
}
